import { Injectable } from '@angular/core';
import { BehaviorSubject, EMPTY, Observable } from 'rxjs';
import { catchError, tap, map, take } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import _ from 'lodash';
import { RestAPIService } from 'app/services/rest-api.service';
import { DataService } from 'app/shared/data/data.service';
import { GetAssetsListInterface, ComponentsByEntityId, TagDetail, Label, PropertiesInterface, LinkTagInterface, IEntity, IGroupTags, IGroupTagsInstances, IGetGroupsAssetsDetail, IGroupProperties, IGroupPropertiesInstances, IGetGroupsNAcls, IAssetProps } from 'app/services/graphqlApi.service';
import { ISummaryLiveDataResp, ISummaryTagInfo, ISummaryMapList, ISummaryMappedData } from 'app/shared/panels/chart/Models/chart-settings.model';
import { getAssetTagsDetailById, getAssetBasicDetailById, getDistinctGroupTags, getDistinctProperties, getGroupPropertiesInstances, getGroupPropertyColNames, getGroupsAssetsDetail, getGroupTagColNames, getGroupTagsInstances, getLinkedTagsByAssetId, getUserGroupsNAclsByAssetId, getGroupTagsByGroupNModelIds, getGroupPropertiesByGroupNModelIds, getPropertiesByIds, getAssetsList } from 'app/shared/constants/gql';
import { AssetsDetails, AssetRequest, IAssetPropOverride, IGroupAssetsDetail, IGroupsNAcls, IGroupAcls } from 'app/assets/interfaces/interfaces';
import { RestRequestType } from 'app/shared/enums/rest-request-type.enum';
import { getComponentInstAlarms, setAlarmForGridView } from 'app/utils/alarms';
import { IUserLogin, IDropdown } from '../../shared/interfaces/model';
import { Property } from 'app/shared/create-properties/interfaces/interfaces';
import { EEntityTypes } from 'app/shared/enums';

@Injectable({
  providedIn: 'root'
})

export class AssetsService {
  private _assets = new BehaviorSubject<AssetsDetails[]>([]);
  readonly assets$ = this._assets.asObservable();

  private _assetDetails = new BehaviorSubject<AssetsDetails>({} as AssetsDetails);
  readonly assetDetails$ = this._assetDetails.asObservable();

  readonly _tags = new BehaviorSubject<TagDetail[]>([]);
  readonly tags$ = this._tags.asObservable();

  readonly _assetComponents = new BehaviorSubject<any[]>([]);
  readonly assetComponents$ = this._assetComponents.asObservable();

  readonly _selectedAssetsTags = new BehaviorSubject<IDropdown[]>([]);
  readonly selectedAssetsTags$ = this._selectedAssetsTags.asObservable();

  readonly _selectedAssetsTagsDetail = new BehaviorSubject<TagDetail[]>([]);
  readonly selectedAssetsTagsDetail$ = this._selectedAssetsTagsDetail.asObservable();

  private readonly _assetAlarms = new BehaviorSubject<any[]>([]);
  readonly assetAlarms$ = this._assetAlarms.asObservable();

  readonly _assetsProperties = new BehaviorSubject<Property[]>([]);
  readonly assetsProperties$ = this._assetsProperties.asObservable();

  readonly _allPerspectiveAssets = new BehaviorSubject<AssetsDetails[]>([]);
  readonly allPerspectiveAssets$ = this._allPerspectiveAssets.asObservable();

  // all assets under a group
  readonly _groupAssetsDetail = new BehaviorSubject<IGroupAssetsDetail[]>([]);
  readonly groupAssetsDetail$ = this._groupAssetsDetail.asObservable();

  // all selected assets of group for asset table
  readonly _selectedAssetsDetail = new BehaviorSubject<IGroupAssetsDetail[]>([]);
  readonly selectedAssetsDetail$ = this._selectedAssetsDetail.asObservable();

  // for permissions tab
  readonly _groupAcls = new BehaviorSubject<IGroupsNAcls>(null);
  readonly groupAcls$ = this._groupAcls.asObservable();

  constructor(
    private readonly apiService: RestAPIService,
    private readonly toastr: ToastrService,
    private readonly dataService: DataService) { }

  createAsset(payload: AssetRequest): Observable<AssetsDetails> {
    return this.apiService.post<AssetsDetails>(EEntityTypes.ASSETS, RestRequestType.CREATE_ASSET, payload)
      .pipe(catchError(err => {
        this.toastr.error(err.error.error?.message);
        return EMPTY;
      }), tap(res => {
        if (res.result.status === 'ok') {
          this.toastr.success('Asset created successfully!');
        } else {
          this.toastr.error(res.error.message);
        }
      }), map(res => res.data));
  }

  updateAsset(payload: AssetRequest) {
    return this.apiService.put<AssetsDetails>(`assets/${payload.id}`, RestRequestType.CREATE_ASSET, payload)
      .pipe(catchError(err => {
        this.toastr.error(err.error.error?.message);
        return EMPTY;
      }),
        tap(res => {
          if (res.result.status === 'ok') {
            this.toastr.success('Asset updated successfully!');
          } else {
            this.toastr.error(res.error.message);
          }
        }),
        map(res => res.data));
  }

  /**
   * delete asset
   * @param assetId asset id
   * @param payload user credentials
   */
  deleteAsset(assetId: string, payload: IUserLogin) {
    return this.apiService.post(`assets/${assetId}/delete`, RestRequestType.DELETE_ASSET, payload)
      .pipe(catchError(err => {
        this.toastr.error(err.error.error?.message);
        return EMPTY;
      }), tap(res => {
        this.toastr.success('Asset deleted successfully!');
         const updatedAssets: any[] = this._assets.getValue().filter(asset => asset.id !== assetId);
        this._assets.next([...updatedAssets]);
      }));
  }

  setTags(tags: TagDetail[]) {
    this._tags.next(tags);
  }

  getTags() {
    return this._tags.getValue();
  }

  /**
   * get asset basic detail by id
   * @param assetId asset id
   */
  getAssetBasicDetailById(assetId: string) {
    return this.apiService.getGQLData({ query: getAssetBasicDetailById.query, params: { assetId: assetId } }).pipe(catchError(err => {
      const message = err.error.error?.message || 'An error has occured.';
      this.toastr.error(message);
      return EMPTY;
    }), map((response) => {
      const queryResponse: GetAssetsListInterface[] = response?.data?.intellija_entities;
      return queryResponse.map(data => !!data && this.mapAssets(data)) || [];
    }));
  }

  /**
   * get asset tags detail by id
   * @param assetId assetId
   */
  getAssetTagsDetailById(assetId: string) {
    return this.apiService.getGQLData({ query: getAssetTagsDetailById.query, params: { assetId: assetId } }).pipe(catchError(err => {
      const message = err.error.error?.message || 'An error has occured.';
      this.toastr.error(message);
      return EMPTY;
    }), map((response) => {
      const queryResponse: GetAssetsListInterface[] = response?.data?.intellija_entities;
      return queryResponse.map(data => !!data && this.mapAssets(data)) || [];
    }), tap(assets => {
      console.log('assets: tap', assets);
      this._tags.next([...assets[0]?.tags, ...assets[0]?.modelTags] || []);
      this.dataService.tags.next(assets[0]?.tags || []);
    }));
  }

  /**
   * remove the duplicates from array of objects
   * @param duplicateComponents array
   */
  getUniqueList(duplicateComponents) {
    const ids = duplicateComponents?.map(o => o.id);
    return duplicateComponents?.filter(({ id }, index) => !ids.includes(id, index + 1))
  }

  /**
   * remove duplicate alarms in case of overriding alarms
   * @param assetAlarms asset alarms
   */
  removeDuplicateAlarmsInOverrideCase(assetAlarms) {
    assetAlarms?.forEach(alarm => {
      alarm["combineName"] = alarm.name + alarm.componentInstanceName;
    });

    // extracting duplicates
    const duplicateAlarms = _.flow([
      arr => _.groupBy(arr, 'combineName'), // group elements by id
      g => _.filter(g, o => o.length > 1), // remove groups that have less than two members
      _.flatten // flatten the results to a single array
    ])(assetAlarms);
    // console.log('duplicateAlarms :>> ', duplicateAlarms);

    // extracting alarms to be deleted
    if (duplicateAlarms?.length) {
      const alarmsToBeDelete = duplicateAlarms.filter(duplicateAlarm =>
        (!duplicateAlarm?.parentConfigId && duplicateAlarm?.isOverride === false));
      // console.log('alarmsToBeDelete :>> ', alarmsToBeDelete);

      // deleting unnecessary(toBeDeleted) alarms
      if (alarmsToBeDelete?.length) {
        assetAlarms.forEach((alarm, index) => {
          alarmsToBeDelete.forEach(delAlarm => {
            if (delAlarm.id === alarm.id && delAlarm.combineName === alarm.combineName) {
              // console.log('alarm 123:>> ', alarm);
              assetAlarms.splice(index, 1);
            }
          });
        });
      }
    }

    const newArray = [...assetAlarms];
    // console.log('newArray 444:>> ', newArray);
    return newArray;
  }

  getTagMappingLinks(entityId: string) {
    console.log('getTagMappingLinks entityId: ', entityId);
    return this.apiService.getGQLData({ query: getLinkedTagsByAssetId.query, params: { assetId: entityId } }).pipe(catchError(err => {
      const message = err.error.error?.message || 'An error has occured.';
      this.toastr.error(message);
      return EMPTY;
    }), map((response) => {
      const queryResponse: LinkTagInterface[] = response?.data?.intellija_tag_mapping_links;
      const tagList = (queryResponse || [])
        .filter(tag => tag.entity_ds_node_link?.entity_id === entityId);
      const recentLinkedTagMap = new Map();
      for (let item of tagList) {
        const tag = this.mapTagMappingLinks(item);
        const compInstTagId = tag.entityTagId + '_' + tag.componentInstanceId;
        if (recentLinkedTagMap.has(compInstTagId)) {
          const oldTag = recentLinkedTagMap.get(compInstTagId)!;
          const timeDiff = new Date(tag.effectiveFrom).getTime() - new Date(oldTag.effectiveFrom).getTime();
          const recentTag = timeDiff > 0 ? tag : oldTag;
          recentLinkedTagMap.set(compInstTagId, recentTag);
        } else {
          recentLinkedTagMap.set(compInstTagId, tag);
        }
      }

      return recentLinkedTagMap;
    }));
  }

  private mapTagMappingLinks(tag) {
    return {
      isLinked: tag.is_linked,
      dsNodeTagId: tag.ds_node_tag_id,
      tagMappinglinkId: tag.id,
      entityTagId: tag.entity_tag_id,
      entityDsNodeLinksId: tag.entity_ds_node_links_id,
      effectiveTo: tag.effective_to,
      effectiveFrom: tag.effective_from,
      dsNodeId: tag.ds_node_tag?.ds_node_id,
      dsNodeTagName: tag.ds_node_tag?.name,
      dsId: tag.ds_node_tag?.ds_node?.ds_id,
      dsName: tag.ds_node_tag?.ds_node?.datasource?.name,
      dsTypeName: tag.ds_node_tag?.ds_node?.datasource?.ref_datasource_type?.name,
      dsTypeId: tag.ds_node_tag?.ds_node?.datasource?.type,
      dsNodeName: tag.ds_node_tag?.ds_node?.name,
      dsNodeType: tag.ds_node_tag?.ds_node?.type,
      tagMappingLinkUomId: tag.validated_ds_uom,
      tagMappingLinkUomName: tag.ref_uom?.name,
      componentInstanceId: tag.component_instance_id,
    };
  }

  loadAllAssets() { // GetAssetsList in graphqlApi
    return this.apiService.getGQLData({ query: getAssetsList.query }).pipe(catchError(err => {
      const message = err.error.error?.message || 'An error has occured.';
      this.toastr.error(message);
      return EMPTY;
    }), map((response) => {
      const queryResponse: GetAssetsListInterface[] = response?.data?.intellija_entities;
      const assets = queryResponse.map(asset => this.mapAssets(asset));
      this._assets.next(assets || []);
    }));
  }

  private mapAssets(asset: GetAssetsListInterface) {
    const alarms = getComponentInstAlarms(asset.entity?.entitiesComponentsByEntityId || []);

    return {
      id: asset.id,
      name: asset.name,
      modelId: asset.entity?.id,
      modelName: asset.entity?.name,
      modelType: asset.entity?.models?.length ? asset.entity.models[0].ref_model_type?.name : '',
      sourceModelId: asset.source_model_id,
      createdBy: asset.created_by,
      createdOn: asset.created_date,
      lastEditedBy: asset.updated_by,
      lastEditedOn: asset.updated_date,
      labels: this.getLabels(asset.labels),
      dsCount: asset.entity_ds_node_links?.length || 0,
      tagsCount: this.getTagsCount(asset.entity?.entitiesComponentsByEntityId || []) + (asset.entity?.tags_aggregate?.aggregate?.count || 0),
      componentsCount: this.getComponentCount(asset.entity?.entitiesComponentsByEntityId || []),
      tags: this.getMappedTags(asset.entity?.entitiesComponentsByEntityId || []),
      modelTags: this.getMappedModelTags(asset.entity, asset.entity.id, asset.entity.name),
      modelTagsCount: asset.entity?.tags?.length,
      components: this.getComponenets(asset.entity?.entitiesComponentsByEntityId || []), // Need to checkwith actual data*********************
      properties: this.getProperties(asset),
      modelProperties: this.getModalProperties(asset.entity?.properties),
      alarmsCount: alarms.length || 0,
      alarms: alarms,
    };
  }

  private getComponenets(components: ComponentsByEntityId[]) {
    return components.reduce((componentList, component) => {
      const compId = component.component_id;
      const compList = component.model_component_instances.map(compInst => {
        return {
          id: compId,
          name: compInst.entities_component?.entity?.name,
          componentInstanceId: compInst.component_instance_id,///////
          componentInstanceName: compInst.name,
        }
      });

      return [...compList, ...componentList];
    }, []);
  }

  private getComponentCount(components: ComponentsByEntityId[]) {
    return components.reduce((count, item) => {
      return count + item.model_component_instances.length;
    }, 0);
  }

  getProperties(asset: GetAssetsListInterface) {
    const compProperties = asset.entity.entitiesComponentsByEntityId.reduce((properties, component) => {
      const props = component.model_component_instances.reduce((compInsProp, compInst) => {
        const entityName = compInst.entities_component.entity?.name;
        const entityId = compInst.entities_component.entity?.id;
        const props = (compInst.entities_component.entity.properties || []).map(p =>
        ({
          ...this.mapProperties(p, entityName, entityId),
          componentInstanceId: compInst.component_instance_id,
          componentInstanceName: compInst.name,
        })
        );
        return [...compInsProp, ...props]
      }, []);

      return [...properties, ...props];
    }, []);

    return compProperties;
  }

  getModalProperties(modelProperties: PropertiesInterface[]) {
    const properties: Property[] = modelProperties?.map(property => {
      return this.mapProperties(property, property.entity?.name, property.entity?.id);
    });

    return properties;
  }

  private mapProperties(property: PropertiesInterface, entityName: string, entityId: string, entity = {}) {
    return {
      id: property.id,
      name: property.name,
      value: property.value,
      dataTypeId: property.data_type,
      propertyType: property.ref_property_type?.name,
      action: null,
      createdBy: property.created_by,
      createdOn: property.created_date,
      lastEditedBy: property.updated_by,
      lastEditedOn: property.updated_date,
      entity_id: property.entity_id,
      entityName,
      entityId,
      entityType: EEntityTypes.ASSETS,
      disableDeleteAction: true,
      entity,
      isOverride: property.is_override,
      parentPropertyId: property.parent_property_id,
    }
  }

  private getMappedTags(components: ComponentsByEntityId[]): TagDetail[] {
    return components.reduce((tags, component) => {
      const cTags = component.model_component_instances.reduce((instTags, comp) => {
        const componentName = comp.entities_component.entity.name;
        const entityId = comp.entities_component.entity.id;

        const compTags = comp.entities_component.entity?.tags?.map(tag => {
          const states = (tag.state_tags || []).map(s =>
          ({
            color: s.color,
            value: s.value,
            isBlink: s.is_blink,
            id: s.id,
            state: s.state
          }));

          return {
            id: tag.id,
            allowWrite: tag.allow_write || false,
            entity_id: tag.entity_id,
            name: tag.name,
            tagTypeId: tag.tag_type_id,
            tagType: tag.ref_tag_type.name,
            displayName: tag.display_name,
            description: tag.description,
            alarms: null,
            action: null,
            historizedId: tag.historized_id,
            displayEuId: tag.display_eu_id,
            factor: tag.factor,
            offset: tag.offset,
            precision: tag.precision,
            rejectMax: tag.reject_max,
            rejectMin: tag.reject_min,
            histPercentChange: tag.hist_percent_change,
            isThousandSeparator: tag.is_thousand_separator || false,
            measurementCategory: tag.measurement_category,
            measurementName: tag.ref_measurement?.name,
            calc_tag_config: tag?.calc_tag_config || null,
            states,
            componentName,
            entityId,
            entityType: 'components',
            isLinked: false,
            datasourceName: 'Source-One',
            datasourceType: 'Sparkplug',
            nodeName: 'node-one',
            dataSourceTagName: 'D-Tag-one',
            componentInstanceId: comp.component_instance_id,
            componentInstanceName: comp.name,
            disableDeleteAction: true,
            alarmsCount: tag.alarms_configs?.length || 0,
          }
        }) || [];

        return [...instTags, ...compTags]
      }, []);

      return [...tags, ...cTags];
    }, []);
  }

  private getMappedModelTags(modelEntity: IEntity, modelId: string = '', modelName: string = ''): TagDetail[] {
    const modelMappedTags: TagDetail[] = [];
    let componentsInstances: string = '';
    modelEntity?.entitiesComponentsByEntityId?.map(componentInstance => {
      componentInstance?.model_component_instances?.map(comp => {
        componentsInstances = componentsInstances ? componentsInstances + ', ' + comp?.name : comp?.name;
      });
    });

    modelEntity?.tags?.map(tag => {
      const states = (tag.state_tags || []).map(s =>
      ({
        color: s.color,
        value: s.value,
        isBlink: s.is_blink,
        id: s.id,
        state: s.state
      }));

      const modelTagObj = {
        id: tag.id,
        allowWrite: tag.allow_write || false,
        entity_id: tag.entity_id,
        name: tag.name,
        tagTypeId: tag.tag_type_id,
        tagType: tag.ref_tag_type.name,
        displayName: tag.display_name,
        description: tag.description,
        alarms: null,
        action: null,
        historizedId: tag.historized_id,
        displayEuId: tag.display_eu_id,
        factor: tag.factor,
        offset: tag.offset,
        precision: tag.precision,
        rejectMax: tag.reject_max,
        rejectMin: tag.reject_min,
        calc_tag_config: tag?.calc_tag_config || null,
        histPercentChange: tag.hist_percent_change,
        isThousandSeparator: tag.is_thousand_separator || false,
        measurementCategory: tag.measurement_category,
        measurementName: tag.ref_measurement?.name,
        componentInstanceId: null, //
        componentInstanceName: '',
        states,
        // componentName,
        // entityId,
        entityType: 'models',
        isLinked: false,
        datasourceName: 'Source-One',
        datasourceType: 'Sparkplug',
        nodeName: 'node-one',
        dataSourceTagName: 'D-Tag-one',
        modelId: modelId,
        modelName: modelName,
        disableDeleteAction: true,
        alarmsCount: tag.alarms_configs?.length || 0,
      }

      modelMappedTags.push(modelTagObj);
    });

    return modelMappedTags;
  }

  private getTagsCount(components: ComponentsByEntityId[]) {
    return components.reduce((count, comp) => {
      const ctCount = comp.model_component_instances.reduce((result, component) => {
        return result += component.entities_component.entity?.tags_aggregate?.aggregate?.count || 0;
      }, 0);
      return count + ctCount;
    }, 0);
  }

  private getLabels(labels: Label[]): string[] {
    return labels?.reduce((result, l) => {
      if (l.label?.name) {
        result.push(l.label.name);
      }

      return result;
    }, []);
  }

  getPropNAlarmsbyAssetId(assetId: string) {
    if (assetId) {
      return this.apiService.get(`assets/${assetId}`)
        .pipe(catchError(err => {
          const message = err.error.error?.message || 'An error has occured.';
          this.toastr.error(message);
          return EMPTY;
        }), map(res => this.setData(res.data) || []), tap((asset: any) => {
          console.log('asset: ', asset);
          this.dataService.alarms.next(asset.alarms || []);
          // this._assetProperties.next(asset.properties || []);
        }))
    } else {
      return EMPTY;
    }
  }

  getAssetLiveStatus(assetId: string) {
    if (assetId) {
      return this.apiService.get(`assets/${assetId}/summary`)
        .pipe(catchError(err => {
          const message = err.error.error?.message || 'An error has occured.';
          this.toastr.error(message);
          return EMPTY;
        }), map(res => this.setAssetLiveStatusData(res.data) || []))
    } else {
      return EMPTY;
    }
  }

  setAssetLiveStatusData(assetSummaryData: ISummaryLiveDataResp): ISummaryMappedData {
    if (assetSummaryData) {
      let linkedTags: ISummaryTagInfo[] = [];
      if (assetSummaryData?.linkedTags?.list?.length) {
        linkedTags = assetSummaryData.linkedTags.list.map((tagInfo: ISummaryMapList) => {
          return { tagId: tagInfo.map.tagId, componentInstanceId: tagInfo.map.componentInstanceId } as ISummaryTagInfo;
        });
      }

      return { onlineStatus: assetSummaryData.onlineStatus, isLinked: assetSummaryData.isLinked, lastTagUpdatedTime: assetSummaryData.lastTagUpdatedTime, lastUnlinkTime: assetSummaryData.lastUnlinkTime, linkedTags: linkedTags };
    }
  }

  setData(assetData) {
    console.log('assetData: ', assetData);
    return {
      properties: this.setAssetProperties(assetData.properties),
      alarms: setAlarmForGridView(assetData.alarms)
    }
  }

  setAssetProperties(properties: any[]) {
    const assetProperties: IAssetPropOverride[] = [];
    properties?.forEach(prop => {
      const assetProperty: IAssetPropOverride = {
        id: prop.id,
        name: prop.name,
        value: prop.value,
        dataTypeId: prop.dataTypeId,
        propertyType: prop.refPropertyType?.map?.name,
        action: null,
        createdBy: prop.updatedBy,
        createdOn: prop.createdDate,
        lastEditedBy: prop.updatedBy,
        lastEditedOn: null,
        entityId: prop.entityId,
        entityType: EEntityTypes.ASSETS,
        disableDeleteAction: true,
        isOverride: prop.isOverride,
        componentInstanceName: prop.componentInstanceName,
        componentInstanceId: prop.componentInstanceId,
        componentName: prop.componentName,
        parentPropertyId: prop.parentPropertyId,
      }

      assetProperties.push(assetProperty);
    })

    return [...assetProperties];
  }

  setGroupAssetsDetail(assetDetails: AssetsDetails[]) {
    let assetTags: IDropdown[] = [];
    const assetsTagsDetail: TagDetail[] = [];
    const assetsProperties: Property[] = [];
    if (assetDetails.length) {
      this._allPerspectiveAssets.next(assetDetails);
      assetDetails.map((asset) => {
        asset.tags?.map((tag: TagDetail) => {
          assetTags.push({ name: tag.name, value: tag.id });
          tag.assetId = asset.id;
          tag.assetName = asset.name;
          tag.modelId = asset.modelId;
          tag.modelName = asset.modelName;
          assetsTagsDetail.push(tag);
        });

        asset.properties?.map((property: Property) => {
          property.assetId = asset.id;
          property.assetName = asset.name;
          property.modelId = asset.modelId;
          property.modelName = asset.modelName;
          property.dataType = property.propertyType;
          assetsProperties.push(property);
        });

        asset.modelProperties?.map((property: Property) => {
          property.assetId = asset.id;
          property.assetName = asset.name;
          property.modelId = asset.modelId;
          property.modelName = asset.modelName;
          property.dataType = property.propertyType;
          // property.type = property.propertyType;
          property.entityType = EEntityTypes.MODELS;
          property.propertyType = EEntityTypes.MODELS;
          assetsProperties.push(property);
        });

        // model tags
        // console.log('asset?.modelTags: ', asset?.modelTags);
        asset.modelTags?.map((tag: TagDetail) => {
          assetTags.push({ name: tag.name, value: tag.id });
          tag.assetId = asset.id;
          tag.assetName = asset.name;
          tag.modelId = asset.modelId;
          tag.modelName = asset.modelName;
          assetsTagsDetail.push(tag);
        });
      });

      // remove duplicate tags (will appear only once)
      const tags: IDropdown[] = [...assetTags?.reduce((unique, o) => {
        if (!unique.some(obj => obj.name === o.name)) {
          unique.push(o);
        }
        return unique;
      }, [])];

      console.log('assetsProperties:123 ', assetsProperties);
      // sort tags by name
      assetTags = _.sortBy([...tags], ["name"]);
      this._assetsProperties.next(assetsProperties);
      this._selectedAssetsTagsDetail.next(assetsTagsDetail);
      this._selectedAssetsTags.next(assetTags);
    } else {
      this._allPerspectiveAssets.next([]);
      this._assetsProperties.next(assetsProperties);
      this._selectedAssetsTagsDetail.next(assetsTagsDetail);
      this._selectedAssetsTags.next(assetTags);
    }
  }

  /**
   * get distinct tags against group
   * @param groupId perspective group id
   */
  getGroupTagsByGroupId(groupId: string) {
    return this.apiService.getGQLData({ query: getDistinctGroupTags.query, params: { perspectiveGroupId: groupId } }).pipe(
      catchError(err => {
        const message = err.error.error?.message || 'An error has occured.';
        this.toastr.error(message);
        return EMPTY;
      }), map((response) => {
        const queryResponse: IGroupTags[] = response.data?.intellija_distinct_group_tags;
        return queryResponse.map(data => {
          return { groupId: data.group_id, tagName: data.tag_name, tagType: data.tag_type }
        }) || [];
      }));
  }

    /**
   * get Group's assets Tags By Group and Model Ids
   * @param groupId perspective group id
   * @param modelIds model ids
   */
  getGroupTagsByGroupNModelIds(groupId: string, modelIds: string[]) {
    return this.apiService.getGQLData({ query: getGroupTagsByGroupNModelIds.query, params: { perspectiveGroupId: groupId, modelIds: modelIds } }).pipe(
      catchError(err => {
        const message = err.error.error?.message || 'An error has occured.';
        this.toastr.error(message);
        return EMPTY;
      }), map((response) => {
        const queryResponse: IGroupTags[] = response.data?.intellija_distinct_group_tags;
        return queryResponse.map(data => {
          return { groupId: data.group_id, tagName: data.tag_name, tagType: data.tag_type }
        }) || [];
      }));
  }

  /**
   * get distinct properties against group
   * @param groupId perspective group id
   */
  getGroupPropertiesByGroupId(groupId: string) {
    return this.apiService.getGQLData({
      query: getDistinctProperties.query, params: { perspectiveGroupId: groupId }
    }).pipe(catchError(err => {
      const message = err.error.error?.message || 'An error has occured.';
      this.toastr.error(message);
      return EMPTY;
    }), map((response) => {
      const queryResponse: IGroupProperties[] = response.data?.intellija_distinct_group_properties;
      return queryResponse.map(data => {
        return { groupId: data.group_id, propertyName: data.property_name, propertyType: data.property_type }
      }) || [];
    }));
  }

  /**
   * get distinct properties against group
   * @param groupId perspective group id
    * @param modelIds model ids
    */
  getGroupPropertiesByGroupNModelIds(groupId: string, modelIds: string[]) {
    return this.apiService.getGQLData({
      query: getGroupPropertiesByGroupNModelIds.query, params: { perspectiveGroupId: groupId, modelIds: modelIds }
    }).pipe(catchError(err => {
      const message = err.error.error?.message || 'An error has occured.';
      this.toastr.error(message);
      return EMPTY;
    }), map((response) => {
      const queryResponse: IGroupProperties[] = response.data?.intellija_distinct_group_properties;
      return queryResponse.map(data => {
        return { groupId: data.group_id, propertyName: data.property_name, propertyType: data.property_type }
      }) || [];
    }));
  }

  /**
   * get group tag instances
   * @param groupId group id
   * @param tagName tag name
   */
  getGroupTagsInstances(groupId: string, tagName: string) {
    return this.apiService.getGQLData({
      query: getGroupTagsInstances.query, params: { perspectiveGroupId: groupId, tagName: tagName }
    }).pipe(catchError(err => {
      const message = err.error.error?.message || 'An error has occured.';
      this.toastr.error(message);
      return EMPTY;
    }), map((response) => {
      const queryResponse: IGroupTagsInstances[] = response.data?.intellija_group_tag_instances;
      return queryResponse?.map(data => this.setGroupTagsViewData(data)) || [];
    }));
  }

  /**
 * get group tag instances
 * @param groupId group id
 * @param tagName tag name
 */
  getGroupTagColNames(groupId: string, tagIds: string[], modelIds: string[], ciIds: string[], cTagIds: string[], cModelIds: string[]) {
    return this.apiService.getGQLData({
      query: getGroupTagColNames.query, params: { perspectiveGroupId: groupId, tagIds: tagIds, modelIds: modelIds, ciIds: ciIds, cTagIds: cTagIds, cModelIds: cModelIds }
    }).pipe(catchError(err => {
      const message = err.error.error?.message || 'An error has occured.';
      this.toastr.error(message);
      return EMPTY;
    }), map((response) => {
      const queryResponse: IGroupTagsInstances[] = response.data?.intellija_group_tag_instances;
      return queryResponse.map(data => this.setGroupTagsViewData(data)) || [];
    }));
  }

  /**
   * set group tags view data
   * @param data resp data
   */
  setGroupTagsViewData(data: IGroupTagsInstances) {
    return {
      assetId: data.asset_id,
      assetName: data.asset_name,
      groupId: data.group_id,
      modelId: data.model_id,
      modelName: data.model_name,
      componentId: data.component_id,
      componentName: data.component_name,
      tagId: data.tag_id,
      tagName: data.tag_name,
      tagType: data.tag_type,
      componentInstanceId: data.component_instance_id,
      componentInstanceName: data.component_instance_name
    }
  }

  /**
 * get group tag instances
 * @param groupId group id
 * @param tagName tag name
 */
  getGroupPropertiesInstances(groupId: string, propertyName: string) {
    return this.apiService.getGQLData({
      query: getGroupPropertiesInstances.query, params: { perspectiveGroupId: groupId, propertyName: propertyName }
    }).pipe(catchError(err => {
      const message = err.error.error?.message || 'An error has occured.';
      this.toastr.error(message);
      return EMPTY;
    }), map((response) => {
      const queryResponse: IGroupPropertiesInstances[] = response.data?.intellija_group_property_instances;
      return queryResponse.map(data => this.setGroupPropertiesViewData(data)) || [];
    }));
  }

  /**
 * get group tag instances
 * @param groupId group id
 * @param tagName tag name
 */
  getGroupPropertyColNames(groupId: string, propertyIds: string[], modelIds: string[], ciIds: string[], mPropertyIds: string[], mModelIds: string[]) {
    return this.apiService.getGQLData({
      query: getGroupPropertyColNames.query, params: { perspectiveGroupId: groupId, propertyIds: propertyIds, modelIds: modelIds, ciIds: ciIds, mPropertyIds: mPropertyIds, mModelIds: mModelIds }
    }).pipe(catchError(err => {
      const message = err.error.error?.message || 'An error has occured.';
      this.toastr.error(message);
      return EMPTY;
    }), map((response) => {
      const queryResponse: IGroupPropertiesInstances[] = response.data?.intellija_group_property_instances;
      return queryResponse?.map(data => this.setGroupPropertiesViewData(data)) || [];
    }));
  }

  /**
   * set group properties view data
   * @param data resp data
   */
  setGroupPropertiesViewData(data: IGroupPropertiesInstances) {
    return {
      assetId: data.asset_id,
      assetName: data.asset_name,
      groupId: data.group_id,
      modelId: data.model_id,
      modelName: data.model_name,
      componentId: data.component_id,
      componentName: data.component_name,
      propertyId: data.property_id,
      propertyName: data.property_name,
      propertyType: data.property_type,
      componentInstanceId: data.component_instance_id,
      componentInstanceName: data.component_instance_name,
      isOverride: data.is_override,
      value: data.property_value
    }
  }

  /**
   * get assets names and model names on group
   * @param assetIds assetIds
   */
  getAssetsDetailByIds(assetIds: string[]) {
    if (assetIds.length) {
      this.getGroupAssetsDetails(assetIds).pipe(take(1)).subscribe((assetDetail: IGroupAssetsDetail[]) => {
        if (assetDetail?.length) {
          this._groupAssetsDetail.next(assetDetail);
        }
      });
    }
  }

  /**
   * get asset names and model names on group
   * @param assetIds assetIds
   */
  getGroupAssetsDetails(assetIds: string[]) {
    return this.apiService.getGQLData({
      query: getGroupsAssetsDetail.query, params: { assetIds: assetIds }
    }).pipe(catchError(err => {
      const message = err.error.error?.message || 'An error has occured.';
      this.toastr.error(message);
      return EMPTY;
    }), map((response) => {
      const queryResponse: IGetGroupsAssetsDetail[] = response.data?.intellija_entities;
      return queryResponse.map(data => {
        return {
          assetId: data.id,
          assetName: data.name,
          modelId: data.entity?.id,
          modelName: data.entity?.name,
        }
      }) || [];
    }));
  }

  /**
 * save Acls
 * @param acls acls list
 */
  saveAcls(acls: IGroupAcls[]) {
    return this.apiService.post<any>('acls/create', RestRequestType.CREATE_ACLS, { acls: acls })
      .pipe(catchError(err => {
        const message = err.error.error?.message || 'An error has occured.';
        this.toastr.error(message);
        return EMPTY;
      }), tap(res => {
        if (res.result.status === 'ok') {
          this.toastr.success('Acls created successfully!');
        } else {
          this.toastr.error(res.error.message);
        }
      }), map(res => res.data));
  }

  /**
 * update Acls
 * @param acls acls list
 */
  updateAcls(acls: IGroupAcls[]) {
    return this.apiService.post<any>('acls/update', RestRequestType.UPDATE_ACLS, { acls: acls })
      .pipe(catchError(err => {
        const message = err.error.error?.message || 'An error has occured.';
        this.toastr.error(message);
        return EMPTY;
      }), tap(res => {
        if (res.result.status === 'ok') {
          this.toastr.success('Acls updated successfully!');
        } else {
          this.toastr.error(res.error.message);
        }
      }), map(res => res.data));
  }

  /**
 * delete Acls
 * @param acls acl ids
 */
  deleteAcls(acls: string[]) {
    return this.apiService.post<any>('acls/delete', RestRequestType.DELETE_ACLS, { acls: acls })
      .pipe(catchError(err => {
        const message = err.error.error?.message || 'An error has occured.';
        this.toastr.error(message);
        return EMPTY;
      }), map(res => {
          this.toastr.success('Acls deleted successfully!');
          return [];
      }));
  }

  /**
   * get all user groups and acls (two queries in one call)
   * @param assetId asset id
   */
  getUserGroupsNAclsByAssetId(assetId: string) {
    return this.apiService.getGQLData({
      query: getUserGroupsNAclsByAssetId.query, params: { assetId: assetId } }).pipe(catchError(err => {
      const message = err.error.error?.message || 'An error has occured.';
      this.toastr.error(message);
      return EMPTY;
    }), map((response) => { return response?.data as IGetGroupsNAcls }));
  }

  /**
   * acls Payload
   * @param aclsData acls data
   * @param isUpdate isupdate
   * @param assetId assetId
   */
  aclsPayload(aclsData: IGroupAcls[], isUpdate: boolean, assetId: string): IGroupAcls[] {
    const aclsGroups: IGroupAcls[] = [];
    aclsData.forEach((group: IGroupAcls) => {
      if ((group.read === true || group.write === true || group.delete === true ) || (isUpdate && group.read === false && group.write === false && group.delete === false && group.id)){
        const aclsObject: IGroupAcls = {
        id: isUpdate ? group.id : undefined,
        userId: isUpdate ? undefined : null,
        userGroupId: isUpdate ? undefined : group.value,
        entityId: isUpdate ? undefined : assetId,
        read: group.read,
        write: group.write,
        delete: group.delete
      }

      aclsGroups.push(aclsObject);
    }
  });

    return aclsGroups;
  }

/**
 * get base properties and override properties by PropertyIds
 * @param propertyIds prpIds
 */
  getPropertiesByIds(propertyIds: string[]) {
    return this.apiService.getGQLData({
      query: getPropertiesByIds.query, params: { propertyIds: propertyIds } }).pipe(catchError(err => {
      const message = err.error.error?.message || 'An error has occured.';
      this.toastr.error(message);
      return EMPTY;
    }), map((response) => { return response.data?.intellija_properties || [] as IAssetProps[] }));
  }

  /**
   * save asset property
   * @param assetDetail asset detail
   */
  saveAssetProperty(assetDetail: IAssetPropOverride) {
    console.log('assetDetail: ', assetDetail);
    return this.apiService.post(`property-override`, RestRequestType.CREATE_OVERRIDE_PROPERTY, assetDetail)
      .pipe(catchError(err => {
        const message = err.error.error?.message || 'An error has occured.';
        this.toastr.error(message);
        return EMPTY;
      }),
        tap(res => {
          if (res.result.status === 'ok') {
            this.toastr.success('Asset Property updated successfully!');

          } else {
            this.toastr.error(res.error.message);
          }
        }),
        map(res => res.data));
  }

  /**
   * update asset property
   * @param assetDetail asset detail
   */
  updateAssetProperty(assetDetail: IAssetPropOverride) {
    console.log('component: update', assetDetail);
    return this.apiService.put(`property-override/${assetDetail.id}`, RestRequestType.UPDATE_OVERRIDE_PROPERTY, assetDetail)
      .pipe(catchError(err => {
        const message = err.error.error?.message || 'An error has occured.';
        this.toastr.error(message);
        return EMPTY;
      }),
        tap(res => {
          if (res.result.status === 'ok') {
            this.toastr.success('Asset Property updated successfully!');
            return res.data;
          } else {
            this.toastr.error(res.error.message);
          }
        }),
        map(res => ({ propertyOverrideId: res.data.id })));
  }

}
